<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ColorPicker from '../../ColorPicker.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const stages = computed(() => {
  const pipelineStages = props.pipeline?.stages || [];
  return [...pipelineStages].sort((a, b) => a.position - b.position);
});

const newStageName = ref('');
const newStageColor = ref('#3B82F6');
const editingStageId = ref(null);
const editingStageName = ref('');
const stageDraftColors = ref({});
const colorUpdateTimers = ref({});
const draggedIndex = ref(null);
const confirmStageDeleteModal = ref(null);

const colors = [
  '#94A3B8',
  '#3B82F6',
  '#8B5CF6',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#EC4899',
  '#14B8A6',
];

const getNextColor = () => {
  const usedColors = stages.value.map(s => s.color);
  const available = colors.filter(c => !usedColors.includes(c));
  return available[0] || colors[stages.value.length % colors.length];
};

const handleAddStage = async () => {
  const name = newStageName.value.trim();
  if (!name) return;

  try {
    await store.dispatch('pipelines/createStage', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      stage: {
        name,
        color: newStageColor.value,
        position: stages.value.length,
      },
    });

    newStageName.value = '';
    newStageColor.value = getNextColor();
    useAlert('Estagio adicionado!');

    await store.dispatch('pipelines/show', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
  } catch (error) {
    useAlert('Erro ao adicionar estagio');
  }
};

const startEdit = stage => {
  editingStageId.value = stage.id;
  editingStageName.value = stage.name;
};

const cancelEdit = () => {
  editingStageId.value = null;
  editingStageName.value = '';
};

const saveEdit = async stage => {
  const name = editingStageName.value.trim();
  if (!name) {
    cancelEdit();
    return;
  }

  try {
    await store.dispatch('pipelines/updateStage', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      stageId: stage.id,
      stage: { name },
    });

    editingStageId.value = null;
    useAlert('Estagio atualizado!');

    await store.dispatch('pipelines/show', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
  } catch (error) {
    useAlert('Erro ao atualizar estagio');
  }
};

const getStageColor = stage => {
  return stageDraftColors.value[stage.id] || stage.color;
};

const persistStageColor = async stage => {
  const color = stageDraftColors.value[stage.id] || stage.color;
  if (!color) return;

  try {
    await store.dispatch('pipelines/updateStage', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      stageId: stage.id,
      stage: { color },
    });
    await store.dispatch('pipelines/show', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
  } catch (error) {
    useAlert('Erro ao atualizar cor do estagio');
  }
};

const queuePersistStageColor = stage => {
  const timer = colorUpdateTimers.value[stage.id];
  if (timer) clearTimeout(timer);

  colorUpdateTimers.value[stage.id] = setTimeout(() => {
    persistStageColor(stage);
    delete colorUpdateTimers.value[stage.id];
  }, 250);
};

const handleStageColorChange = (stage, color) => {
  stageDraftColors.value = {
    ...stageDraftColors.value,
    [stage.id]: color,
  };
  queuePersistStageColor(stage);
};

const handleDelete = async stage => {
  const confirmed = await confirmStageDeleteModal.value.showConfirmation();
  if (!confirmed) return;

  try {
    await store.dispatch('pipelines/deleteStage', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      stageId: stage.id,
    });

    useAlert('Estagio excluido!');

    await store.dispatch('pipelines/show', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
  } catch (error) {
    useAlert('Erro ao excluir estagio');
  }
};

// Drag & Drop
const handleDragStart = (event, index) => {
  draggedIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
};

const handleDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const handleDrop = async (event, dropIndex) => {
  event.preventDefault();

  if (draggedIndex.value === null || draggedIndex.value === dropIndex) {
    draggedIndex.value = null;
    return;
  }

  const stagesCopy = [...stages.value];
  const draggedStage = stagesCopy[draggedIndex.value];

  stagesCopy.splice(draggedIndex.value, 1);
  stagesCopy.splice(dropIndex, 0, draggedStage);

  try {
    const updates = stagesCopy.map((stage, index) => ({
      id: stage.id,
      position: index,
    }));

    await store.dispatch('pipelines/reorderStages', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
      stages: updates,
    });

    useAlert('Ordem atualizada!');

    await store.dispatch('pipelines/show', {
      accountId: accountId.value,
      pipelineId: props.pipeline.id,
    });
  } catch (error) {
    useAlert('Erro ao reordenar estagios');
  }

  draggedIndex.value = null;
};

onMounted(() => {
  newStageColor.value = getNextColor();
});

onBeforeUnmount(() => {
  Object.values(colorUpdateTimers.value).forEach(timer => {
    clearTimeout(timer);
  });
  colorUpdateTimers.value = {};
});
</script>

<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-bold text-n-slate-12 mb-1">
        {{ $t('PIPELINES.STAGES_TAB.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('PIPELINES.STAGES_TAB.DESCRIPTION') }}
      </p>
    </div>

    <!-- Add Stage -->
    <div class="flex gap-2 max-w-2xl">
      <ColorPicker v-model="newStageColor" class="flex-shrink-0" />
      <input
        v-model="newStageName"
        type="text"
        :placeholder="$t('PIPELINES.STAGES_TAB.NEW_STAGE_PLACEHOLDER')"
        class="flex-1 px-3 py-2.5 text-sm border border-n-weak rounded-lg bg-n-surface-1 hover:border-n-slate-6 focus:outline-none focus:border-n-brand transition-colors"
        @keyup.enter="handleAddStage"
      />
      <Button icon="i-lucide-plus" @click="handleAddStage">
        {{ $t('PIPELINES.STAGES_TAB.ADD_BUTTON') }}
      </Button>
    </div>

    <!-- Stages List -->
    <div class="space-y-2 max-w-2xl">
      <div
        v-for="(stage, index) in stages"
        :key="stage.id"
        class="flex items-center gap-3 p-3 border border-n-weak rounded-lg bg-n-surface-1 group hover:border-n-brand transition-colors"
        :class="{
          'cursor-move': true,
          'opacity-50': draggedIndex === index
        }"
        draggable="true"
        @dragstart="handleDragStart($event, index)"
        @dragover="handleDragOver($event)"
        @drop="handleDrop($event, index)"
      >
        <!-- Drag Handle -->
        <span class="i-lucide-grip-vertical size-4 text-n-slate-10 flex-shrink-0" />

        <!-- Color -->
        <ColorPicker
          :model-value="getStageColor(stage)"
          class="flex-shrink-0 [&>button]:w-8 [&>button]:h-8 [&>button]:rounded-md [&>button]:border [&>button]:border-n-weak"
          @update:model-value="handleStageColorChange(stage, $event)"
        />

        <!-- Name (Edit Mode) -->
        <input
          v-if="editingStageId === stage.id"
          v-model="editingStageName"
          type="text"
          class="flex-1 px-2 py-1 text-sm border border-n-brand rounded bg-n-surface-1 focus:outline-none"
          autofocus
          @keyup.enter="saveEdit(stage)"
          @keyup.escape="cancelEdit"
          @blur="saveEdit(stage)"
        />

        <!-- Name (View Mode) -->
        <button
          v-else
          type="button"
          class="flex-1 text-sm font-medium text-n-slate-12 text-left hover:underline"
          @click.stop="startEdit(stage)"
        >
          {{ stage.name }}
        </button>

        <!-- Actions -->
        <div
          class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <template v-if="editingStageId === stage.id">
            <Button
              variant="ghost"
              icon="i-lucide-check"
              size="xs"
              @click="saveEdit(stage)"
            />
            <Button
              variant="ghost"
              icon="i-lucide-x"
              size="xs"
              @click="cancelEdit"
            />
          </template>

          <template v-else>
            <Button
              variant="ghost"
              icon="i-lucide-trash-2"
              size="xs"
              color="ruby"
              @click="handleDelete(stage)"
            />
          </template>
        </div>
      </div>

      <p
        v-if="stages.length === 0"
        class="text-sm text-n-slate-11 text-center py-8"
      >
        {{ $t('PIPELINES.STAGES_TAB.EMPTY') }}
      </p>
    </div>
  </div>

  <ConfirmationModal
    ref="confirmStageDeleteModal"
    :title="$t('PIPELINES.STAGES_TAB.DELETE_CONFIRM.TITLE')"
    :description="$t('PIPELINES.STAGES_TAB.DELETE_CONFIRM.MESSAGE')"
    :confirm-label="$t('PIPELINES.STAGES_TAB.DELETE_CONFIRM.YES')"
    :cancel-label="$t('PIPELINES.STAGES_TAB.DELETE_CONFIRM.NO')"
  />
</template>
